import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useLocation, useNavigate } from "react-router-dom";
import {
  Bell,
  PanelLeftClose,
  PanelLeftOpen,
  ChevronDown,
  LogOut,
  Building2,
  Store,
  Warehouse,
  Lock,
  LayoutDashboard,
  FileText,
  Package,
  User,
  Sun,
  GitBranch,
  Check,
  Calculator
} from 'lucide-react';
import { WarehouseContext } from '../../Utility/UserContext';
import { getSubCompany, getShopsByCom, getNotification, Updatepassword } from '../../Utility/Api';
import { Logoutmodel } from './Functions';
import { toast } from 'react-toastify';
import { shop_id, setLocalStorage } from '../../Utility/function';
import { cn } from '../../lib/utils';

const Header = ({ toggleSidebar, isSidebarOpen }) => {
  const navigate = useNavigate();
  const {
    shopItem,
    setShopItem,
    subCompany,
    setSubCompany,
    UserItem,
  } = useContext(WarehouseContext);

  const [subCompanies, setSubCompanies] = useState([]);
  const [branches, setBranches] = useState([]);
  const [notifications, setNotifications] = useState({ document: 0, salary: 0, products: 0 });
  console.log("notifications", notifications)
  const [showNotifications, setShowNotifications] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [deletemodel, setDeletemodel] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordData, setPasswordData] = useState({ new: "", confirm: "" });

  const [openSelector, setOpenSelector] = useState(null);

  // Initialize Data
  useEffect(() => {
    const initialize = async () => {
      await fetchInitialData();
      fetchAlerts();
    };
    initialize();
  }, []);

  // Cascading fetch: Branches whenever subCompany changes
  useEffect(() => {
    if (subCompany?.id) {
      fetchBranches(subCompany.id);
    }
  }, [subCompany]);

  const fetchInitialData = async () => {
    try {
      const response = await getSubCompany();
      if (response.data && response.data.data) {
        const fetchedSubCompanies = response.data.data;
        setSubCompanies(fetchedSubCompanies);

        // Auto-select first SubCompany if none selected
        if (!subCompany && fetchedSubCompanies.length > 0) {
          // handleSubCompanySelect(fetchedSubCompanies[0], true);

          handleSubCompanySelect(fetchedSubCompanies[0], true);
        }
      }
    } catch (error) {
      console.error('Error fetching sub-companies:', error);
    }
  };

  const fetchBranches = async (subId) => {
    try {
      const response = await getShopsByCom(subId);
      if (response.data && response.data.data) {
        const fetchedBranches = response.data.data.map(item => ({
          ...item,
          shop_id: item.id,
          shop_title: item.title
        }));
        setBranches(fetchedBranches);

        // Auto-select first Branch if none selected for this sub-company
        if (fetchedBranches.length > 0) {
          const isCurrentBranchValid = shopItem && fetchedBranches.some(b => b.id === shopItem.id);
          if (!isCurrentBranchValid) {
            // handleBranchSelect(fetchedBranches[0], true);

            handleBranchSelect(fetchedBranches[0], true);
          }
        } else {
          setShopItem(null);
          setLocalStorage("SHOP", "null");
        }
      } else {
        setBranches([]);
        setShopItem(null);
        setLocalStorage("SHOP", "null");
      }
    } catch (error) {
      console.error('Error fetching branches:', error);
      setBranches([]);
      setShopItem(null);
      setLocalStorage("SHOP", "null");
    }
  };

  const handleSubCompanySelect = (sc, reload = true) => {
    setSubCompany(sc);
    setLocalStorage("SUB-COMPANY", JSON.stringify(sc));
    setOpenSelector(null);
    if (reload) window.location.reload();
  };

  const handleBranchSelect = (branch, reload = true) => {
    setShopItem(branch);
    setLocalStorage("SHOP", JSON.stringify(branch));
    setOpenSelector(null);
    if (reload) window.location.reload();
  };

  const fetchAlerts = useCallback(async () => {
    try {
      const response = await getNotification(shop_id);
      if (response && response.data) {
        setNotifications(response.data);
      }
    } catch (error) {
      console.error('Error fetching alerts:', error);
    }
  }, []);

  const totalNotifications = notifications.document + notifications.salary + notifications.products;

  const confirmLogout = () => {
    sessionStorage.clear();
    localStorage.clear();
    window.location.href = '/login';
  };

  const handlePasswordUpdate = async () => {
    if (passwordData.new !== passwordData.confirm) {
      toast.error("Passwords do not match");
      return;
    }
    try {
      await Updatepassword({
        employee_id: UserItem?.user_id,
        new_password: passwordData.new
      });
      toast.success("Password updated successfully");
      setShowPasswordModal(false);
      setPasswordData({ new: "", confirm: "" });
    } catch (error) {
      toast.error("Failed to update password");
    }
  };

  return (
    <>
      <header className={cn(
        "bg-white border-b border-slate-100 sticky top-0 z-30 px-4 flex items-center justify-between shadow-[0_1px_2px_rgba(0,0,0,0.03)]",
        isSidebarOpen
          ? "min-h-14 py-2 lg:py-0 lg:h-14"
          : "min-h-14 py-2 sm:py-0 sm:h-14"
      )}>
        {/* Left side: Toggle & Selectors */}
        <div className="flex items-center gap-6">
          <button
            onClick={toggleSidebar}
            className="p-1.5 text-slate-500 hover:bg-slate-50 rounded-md transition-colors"
          >
            {/* <PanelLeft className="w-5 h-5" /> */}
            {isSidebarOpen ? (
              <PanelLeftClose className="w-5 h-5" />
            ) : (
              <PanelLeftOpen className="w-5 h-5" />
            )}
          </button>

          {/* Breadcrumb Selectors - Shop (SubCompany) and Branch (Shop) */}
          <div className={cn(
            "flex flex-col items-start gap-2 my-1",
            isSidebarOpen
              ? "lg:flex-row lg:items-center lg:gap-4 lg:my-0"
              : "sm:flex-row sm:items-center sm:gap-4 sm:my-0"
          )}>
            {/* Shop Selector (Sub Company) */}
            <div className="flex items-center gap-1.5">
              <Store className="h-4 w-4 text-slate-400" />
              <div className="relative">
                <button
                  onMouseEnter={() => {
                    if (openSelector !== 'subcompany') fetchInitialData();
                  }}
                  onClick={() => {
                    if (openSelector !== 'subcompany') fetchInitialData();
                    setOpenSelector(openSelector === 'subcompany' ? null : 'subcompany');
                  }}
                  className={cn(
                    "flex items-center justify-between gap-1 px-3 h-8 w-[140px] bg-white border border-slate-200 rounded text-slate-600 hover:border-slate-300 transition-all text-xs"
                  )}
                >
                  <span className="truncate">
                    {subCompany?.title || "Select Shop"}
                  </span>
                  <ChevronDown className={cn("w-3 h-3 text-slate-400 transition-transform duration-200", openSelector === 'subcompany' && "rotate-180")} />
                </button>

                {openSelector === 'subcompany' && (
                  <>
                    <div className="fixed inset-0 z-10" onClick={() => setOpenSelector(null)}></div>
                    <div className="absolute left-0 mt-1 w-64 bg-white rounded shadow-xl border border-slate-200 py-1.5 z-20 overflow-hidden animate-in fade-in zoom-in-95 duration-200 origin-top">
                      {subCompanies.map((sc) => (
                        <button
                          key={sc.id}
                          onClick={() => handleSubCompanySelect(sc)}
                          className={cn(
                            "w-full px-4 py-2 text-left text-sm transition-colors flex items-center justify-between hover:bg-slate-50",
                            subCompany?.id === sc.id ? "text-blue-600 bg-blue-50/30 font-bold" : "text-slate-600"
                          )}
                        >
                          <span className="flex items-center gap-2">
                            {subCompany?.id === sc.id && <Check className="w-3.5 h-3.5" />}
                            {sc.title}
                          </span>
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Branch Selector (Shop from API) */}
            <div className="flex items-center gap-1.5">
              <Building2 className="h-4 w-4 text-slate-400" />
              <div className="relative">
                <button
                  onMouseEnter={() => {
                    if (openSelector !== 'branch' && subCompany?.id) fetchBranches(subCompany.id);
                  }}
                  onClick={() => {
                    if (openSelector !== 'branch' && subCompany?.id) fetchBranches(subCompany.id);
                    setOpenSelector(openSelector === 'branch' ? null : 'branch');
                  }}
                  className={cn(
                    "flex items-center justify-between gap-1 px-3 h-8 w-[140px] bg-white border border-slate-200 rounded text-slate-600 hover:border-slate-300 transition-all text-xs"
                  )}
                >
                  <span className="truncate">
                    {shopItem?.shop_title || "Select Branch"}
                  </span>
                  <ChevronDown className={cn("w-3 h-3 text-slate-400 transition-transform duration-200", openSelector === 'branch' && "rotate-180")} />
                </button>

                {openSelector === 'branch' && (
                  <>
                    <div className="fixed inset-0 z-10" onClick={() => setOpenSelector(null)}></div>
                    <div className="absolute left-0 mt-1 w-64 bg-white rounded shadow-xl border border-slate-200 py-1.5 z-20 overflow-hidden animate-in fade-in zoom-in-95 duration-200 origin-top">
                      {branches.length > 0 ? branches.map((b) => (
                        <button
                          key={b.id}
                          onClick={() => handleBranchSelect(b)}
                          className={cn(
                            "w-full px-4 py-2 text-left text-sm transition-colors flex items-center justify-between hover:bg-slate-50",
                            shopItem?.id === b.id ? "text-blue-600 bg-blue-50/30 font-bold" : "text-slate-600"
                          )}
                        >
                          <span className="flex items-center gap-2">
                            {shopItem?.id === b.id && <Check className="w-3.5 h-3.5" />}
                            {b.shop_title}
                          </span>
                        </button>
                      )) : (
                        <div className="px-4 py-2 text-xs text-slate-400 italic">No branches found</div>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Right side: Actions & Profile */}
        <div className="flex items-center gap-4">
          <div className="hidden sm:flex flex-col items-end mr-1 shrink-0 whitespace-nowrap">
            <span className="text-sm font-bold text-slate-800 leading-none">{UserItem?.full_name || "Super Admin"}</span>
            <span className="text-[10px] font-medium text-slate-400 uppercase tracking-tighter">({UserItem?.role_title || "super admin"})</span>
          </div>

          <div className="relative">
            <button
              onClick={() => { setShowNotifications(!showNotifications); setOpenSelector(null); }}
              className="p-2 text-slate-400 hover:text-slate-600 relative transition-colors"
            >
              <Bell className="w-5 h-5" />
              {totalNotifications > 0 && (
                <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-red-500 rounded-full border-2 border-white"></span>
              )}
            </button>

            {showNotifications && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setShowNotifications(false)}></div>
                <div className="absolute right-0 mt-2 w-48 bg-white rounded shadow-lg border border-slate-200 py-2 z-20 overflow-hidden">
                  <div className="px-4 py-2 hover:bg-slate-50 transition-colors flex items-center gap-3">
                    <i className="bi bi-file-earmark-text text-slate-600"></i>
                    <span className="text-sm text-slate-600">Documents : {notifications.document}</span>
                  </div>
                  <div className="px-4 py-2 hover:bg-slate-50 transition-colors flex items-center gap-3">
                    <i className="bi bi-cash-stack text-slate-600"></i>
                    <span className="text-sm text-slate-600">Salary : {notifications.salary}</span>
                  </div>
                  <div className="px-4 py-2 hover:bg-slate-50 transition-colors flex items-center gap-3">
                    <i className="bi bi-box-seam text-slate-600"></i>
                    <span className="text-sm text-slate-600">Products : {notifications.products || 0}</span>
                  </div>
                </div>
              </>
            )}
          </div>

          <div className="relative">
            <button
              onClick={() => { setShowProfileMenu(!showProfileMenu); setOpenSelector(null); }}
              className="flex items-center gap-2 p-1 rounded-full hover:bg-slate-100 transition-all"
            >
              <div className="w-8 h-8 rounded-full bg-[#EBF3FF] flex items-center justify-center text-[#2B7FFF] border border-[#D0E3FF]">
                <User className="w-4 h-4" />
              </div>
              <ChevronDown className="w-3.5 h-3.5 text-slate-400" />
            </button>

            {showProfileMenu && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setShowProfileMenu(false)}></div>
                <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-2xl border border-slate-200 py-1 z-20 overflow-hidden">
                  <div className="px-4 py-2.5 bg-[#F8F9FA] border-b border-slate-100 mb-1">
                    <h3 className="text-xs font-bold text-slate-800 uppercase tracking-wider">My Account</h3>
                  </div>
                  <button
                    onClick={() => { setShowPasswordModal(true); setShowProfileMenu(false); }}
                    className="w-full px-4 py-2.5 text-left text-sm text-slate-600 hover:bg-slate-50 flex items-center gap-3 transition-colors"
                  >
                    <Lock className="w-4 h-4 text-slate-400" /> Change Password
                  </button>
                  <button
                    onClick={() => setDeletemodel(true)}
                    className="w-full px-4 py-2.5 text-left text-sm text-red-500 hover:bg-red-50 font-semibold flex items-center gap-3 transition-colors"
                  >
                    <LogOut className="w-4 h-4" /> Logout
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Logout Modal */}
      {deletemodel && (
        <Logoutmodel
          setShowModal={setDeletemodel}
          showModal={deletemodel}
          handleDelete={confirmLogout}
        />
      )}

      {/* Password Reset Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6">
            <h3 className="text-xl font-bold text-slate-800 mb-6">Security Update</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">New Password</label>
                <input
                  type="password"
                  className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-lg outline-none focus:ring-2 focus:ring-blue-500/20"
                  value={passwordData.new}
                  onChange={(e) => setPasswordData({ ...passwordData, new: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Confirm Password</label>
                <input
                  type="password"
                  className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-lg outline-none focus:ring-2 focus:ring-blue-500/20"
                  value={passwordData.confirm}
                  onChange={(e) => setPasswordData({ ...passwordData, confirm: e.target.value })}
                />
              </div>
            </div>
            <div className="flex gap-3 mt-8">
              <button onClick={() => setShowPasswordModal(false)} className="flex-1 py-2.5 rounded-lg font-bold text-slate-600 hover:bg-slate-100">Cancel</button>
              <button onClick={handlePasswordUpdate} className="flex-1 py-2.5 rounded-lg bg-blue-600 text-white font-bold">Update</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const ChevronRightIcon = () => (
  <div className="text-slate-300">
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m9 18 6-6-6-6" /></svg>
  </div>
);

export default Header;
